import { Button, message, Modal, Space, Table, TableProps } from "antd";
import {
  useDeleteInboundRemittanceMutation,
  useGetInboundRemittanceQuery,
  useLazyDownloadInboundRemittanceQuery
} from "../../../redux/api/reconciliationsApiSlice";
import { GetInboundRemittanceApiParams, InboundRemittance, RemittanceType } from "../../../types/reconciliation";
import React, { Key, useMemo } from "react";
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useInboundRemittanceTable } from "../../../context/InboundRemittanceTableContext";
import FilterBySearch from "../../tables/negotiationsTables/generalTable/FilterBySearch";
import { getColumnDateProps } from "../../tables/negotiationsTables/columnsUtils";
import { isEmpty } from "lodash";
import { printValue } from "utils/reconciliationTableUtils";
import { RemittanceNotesButton } from "./RemittanceNotesButton";

export const RemittanceTable = ({ onRecordDetailClick, defaultFilters }: {
  onRecordDetailClick?: (record: InboundRemittance) => void,
  defaultFilters?: Partial<GetInboundRemittanceApiParams>,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [modalApi, modalContext] = Modal.useModal();

  const {
    name,
    setName,
    userCreation,
    setUserCreation,
    dateCreation,
    setDateCreation,
    setOrdering,
    getParamsForApiRequest
  } = useInboundRemittanceTable();

  const { data: remittanceList, isLoading } = useGetInboundRemittanceQuery(getParamsForApiRequest(defaultFilters));
  const [downloadRemittance] = useLazyDownloadInboundRemittanceQuery();
  const [deleteRemittance, { isLoading: isDeletingRemittance }] = useDeleteInboundRemittanceMutation();

  const columns: TableProps<InboundRemittance>["columns"] = useMemo(() => (
    [
      {
        key: "name",
        dataIndex: "name",
        title: "Nome rimessa",
        ellipsis: true,
        render: (value) => printValue(value, "text"),
        sorter: { multiple: 1 },
        filteredValue: name,
        filterDropdown: (props) => {
          return <FilterBySearch
            props={props}
            placeholder={"Filtra per il nome della rimessa..."} />;
        }
      },
      {
        key: "user_creation",
        dataIndex: "user_creation",
        title: "Utente creazione",
        ellipsis: true,
        render: (value) => printValue(value, "text"),
        sorter: { multiple: 2 },
        filteredValue: userCreation,
        filterDropdown: (props) => {
          return <FilterBySearch
            props={props}
            placeholder={"Filtra per il nome dell'utente che ha creato la rimessa..."} />;
        }
      },
      {
        key: "date_creation",
        dataIndex: "date_creation",
        title: "Data creazione",
        ellipsis: true,
        sorter: { multiple: 13 },
        filteredValue: dateCreation,
        ...getColumnDateProps("title_effective_date", "day"),
        render: (value) => printValue(value, "date", { dateTemplate: "DD/MM/YYYY" })
      },
      {
        key: "actions",
        width: defaultFilters?.remittance_type === RemittanceType.archived ? 180 : 130,
        render: (_, record) => (
          <Space size={"small"}>
            <Button type={"text"} icon={<EyeOutlined />} title={"Visualizza titoli"}
                    onClick={() => {
                      onRecordDetailClick?.(record);
                    }} />
            {defaultFilters?.remittance_type === RemittanceType.standard &&
              <Button type={"text"} icon={<DownloadOutlined />} title={"Scarica rimessa"}
                      onClick={() => {
                        downloadRemittance(record.uuid).unwrap().catch((err) => {
                          console.error("could not download remittance due to an error: ", err);
                          messageApi.error("Errore nel download del file");
                        });
                      }} />}
            {defaultFilters?.remittance_type === RemittanceType.archived && (
              <RemittanceNotesButton remittance={record} />
            )}
            <Button
              danger={true}
              icon={<DeleteOutlined />}
              type={"text"}
              onClick={() => {
                const isArchivedView = defaultFilters?.remittance_type === RemittanceType.archived;
                modalApi.warning({
                  title: "Elimina rimessa",
                  closable: true,
                  content: isArchivedView ? "Sei sicuro di voler eliminare la rimessa archiviata? Dovrai far riconciliare nuovamente i broker." : "Sei sicuro di voler eliminare la rimessa? tutti i titoli confermati verranno invalidati da parte del sistema, dovrai creare una nuova rimessa.",
                  onOk: () => {
                    deleteRemittance(record.uuid).unwrap().catch(err => {
                      console.error("Could not delete remittance due to an error: ", err);
                      messageApi.error("Impossibile eliminare la rimessa");
                    });
                  },
                  okText: "Elimina",
                  okButtonProps: {
                    danger: true,
                    loading: isDeletingRemittance,
                    disabled: isDeletingRemittance
                  },
                  cancelText: "No"
                });
              }} />

          </Space>
        )
      }
    ]
  ), [dateCreation, defaultFilters?.remittance_type, deleteRemittance, downloadRemittance, isDeletingRemittance, messageApi, modalApi, name, onRecordDetailClick, userCreation]);

  return (
    <>
      {modalContext}
      {contextHolder}

      <Table<InboundRemittance>
        dataSource={remittanceList?.results}
        loading={isLoading}
        columns={columns}
        rowKey={"uuid"}
        onChange={(_, filters, sorter) => {
          // Gestione ordinamento
          const buildOrderingString = (sorterValue: any): string => {
            if (Array.isArray(sorterValue)) {
              return sorterValue
                .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
                .join(",");
            } else if (sorterValue.order) {
              return sorterValue.order === "descend" ? `-${sorterValue.field}` : `${sorterValue.field}`;
            }
            return "";
          };

          // Applicazione ordinamento
          setOrdering(buildOrderingString(sorter));

          // Gestione filtri
          if (filters) {
            // Mappa delle funzioni setter per ogni tipo di filtro
            const filterSetters: Record<string, (value: Key[]) => void> = {
              name: setName,
              user_creation: setUserCreation,
              date_creation: setDateCreation
            };

            // Applica i setter per ogni filtro
            Object.entries(filterSetters).forEach(([filterName, setter]) => {
              const filterValue = filters[filterName];
              setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
            });
          }
        }} />
    </>

  );
};